import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: true,
  error: null
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      localStorage.setItem('token', action.payload.token);
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        error: null
      };
    case 'LOGIN_FAIL':
      localStorage.removeItem('token');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: action.payload
      };
    case 'LOGOUT':
      localStorage.removeItem('token');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: null
      };
    case 'LOAD_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        loading: false
      };
    case 'AUTH_ERROR':
      localStorage.removeItem('token');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: action.payload
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload
      };
    case 'UPDATE_PHOTO':
      return {
        ...state,
        user: {
          ...state.user,
          photoUrl: action.payload
        }
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Set token in axios header
  useEffect(() => {
    if (state.token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [state.token]);

  // Load user on app start
  useEffect(() => {
    const loadUser = async () => {
      if (state.token) {
        try {
          const response = await axios.get('/api/users/profile');
          dispatch({ type: 'LOAD_USER', payload: response.data });
        } catch (error) {
          dispatch({ type: 'AUTH_ERROR', payload: error.response?.data?.error || 'Failed to load user' });
        }
      } else {
        dispatch({ type: 'AUTH_ERROR', payload: null });
      }
    };

    loadUser();
  }, [state.token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password });
      dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
      return { success: true };
    } catch (error) {
      let errorMessage = 'Login failed';
      
      if (error.response?.data?.error) {
        const serverError = error.response.data.error;
        
        // Handle specific login error cases
        if (serverError.includes('Invalid credentials') || serverError.includes('credentials')) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (serverError.includes('User not found')) {
          errorMessage = 'No account found with this email address. Please register first.';
        } else {
          errorMessage = serverError;
        }
      }
      
      dispatch({ type: 'LOGIN_FAIL', payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await axios.post('/api/auth/register', { name, email, password });
      
      // Handle new email verification system
      if (response.data.requiresVerification) {
        // For now, just log the user in but show verification message
        dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
        return { 
          success: true, 
          requiresVerification: true,
          message: response.data.message 
        };
      } else {
        // Old behavior for already verified users
        dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
        return { success: true };
      }
    } catch (error) {
      let errorMessage = 'Registration failed';
      
      if (error.response?.data?.error) {
        const serverError = error.response.data.error;
        
        // Handle specific error cases with user-friendly messages
        if (serverError.includes('Email already registered') || serverError.includes('already exists')) {
          errorMessage = 'This email address is already registered. Please use a different email or try logging in.';
        } else if (serverError.includes('Password') || serverError.includes('password')) {
          errorMessage = 'Password must be at least 6 characters long.';
        } else if (serverError.includes('Validation failed')) {
          // Extract validation details if available
          const details = error.response.data.details;
          if (details && details.length > 0) {
            errorMessage = details.map(d => d.msg).join(', ');
          } else {
            errorMessage = 'Please check your input and try again.';
          }
        } else {
          errorMessage = serverError;
        }
      }
      
      dispatch({ type: 'LOGIN_FAIL', payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    dispatch({ type: 'LOGOUT' });
  };

  const updateUser = async (userData) => {
    try {
      const response = await axios.put('/api/users/profile', userData);
      dispatch({ type: 'UPDATE_USER', payload: response.data });
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to update profile';
      return { success: false, error: errorMessage };
    }
  };

  const uploadPhoto = async (file) => {
    try {
      const formData = new FormData();
      formData.append('photo', file);
      
      const response = await axios.post('/api/users/upload-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      dispatch({ type: 'UPDATE_PHOTO', payload: response.data.photoUrl });
      return { success: true, photoUrl: response.data.photoUrl };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to upload photo';
      return { success: false, error: errorMessage };
    }
  };

  const deletePhoto = async () => {
    try {
      await axios.delete('/api/users/delete-photo');
      dispatch({ type: 'UPDATE_PHOTO', payload: null });
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete photo';
      return { success: false, error: errorMessage };
    }
  };

  const createGroup = async (title, isPublic = false, isSpecial = false) => {
    try {
      const response = await axios.post('/api/groups', { title, isPublic, isSpecial });
      return { success: true, group: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to create group';
      return { success: false, error: errorMessage };
    }
  };

  const joinGroup = async (code) => {
    try {
      const response = await axios.post('/api/groups/join', { code });
      return { success: true, group: response.data.group };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to join group';
      return { success: false, error: errorMessage };
    }
  };

  const getMyGroup = async () => {
    try {
      const response = await axios.get('/api/groups/my-group');
      return { success: true, group: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to get group';
      return { success: false, error: errorMessage };
    }
  };

  // MODIFIED: Added pagination support and search functionality
  const getPublicGroups = async (page = 1, limit = 10, search = '') => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      
      if (search.trim()) {
        params.append('search', search.trim());
      }
      
      const response = await axios.get(`/api/groups/public?${params.toString()}`);
      return { 
        success: true, 
        groups: response.data.groups,
        pagination: response.data.pagination
      };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to get public groups';
      return { success: false, error: errorMessage };
    }
  };

  const joinPublicGroup = async (groupId) => {
    try {
      const response = await axios.post('/api/groups/join-public', { groupId });
      return { success: true, group: response.data.group };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to join group';
      return { success: false, error: errorMessage };
    }
  };

  const leaveGroup = async (groupId = null) => {
    try {
      const url = groupId ? `/api/groups/leave?groupId=${groupId}` : '/api/groups/leave';
      await axios.delete(url);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to leave group';
      return { success: false, error: errorMessage };
    }
  };

  // NEW: Get all user groups
  const getUserGroups = async () => {
    try {
      const response = await axios.get('/api/groups/user-groups');
      return { success: true, groups: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to get user groups';
      return { success: false, error: errorMessage };
    }
  };

  // NEW: Set active group
  const setActiveGroup = async (groupId) => {
    try {
      const response = await axios.post('/api/groups/set-active', { groupId });
      return { success: true, message: response.data.message };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to set active group';
      return { success: false, error: errorMessage };
    }
  };

  // MODIFIED: Get events with optional group filter
  const getEvents = async (groupId = null) => {
    try {
      const url = groupId ? `/api/events?groupId=${groupId}` : '/api/events';
      const response = await axios.get(url);
      return { success: true, events: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to get events';
      return { success: false, error: errorMessage };
    }
  };

  // NEW: Edit group
  const editGroup = async (groupId, title, isPublic = null, isSpecial = null) => {
    try {
      const data = { title };
      if (isPublic !== null) {
        data.isPublic = isPublic;
      }
      if (isSpecial !== null) {
        data.isSpecial = isSpecial;
      }
      const response = await axios.put(`/api/groups/${groupId}`, data);
      return { success: true, group: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to edit group';
      return { success: false, error: errorMessage };
    }
  };

  // NEW: Delete group
  const deleteGroup = async (groupId) => {
    try {
      const response = await axios.delete(`/api/groups/${groupId}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete group';
      return { success: false, error: errorMessage };
    }
  };

  // NEW: Upload group logo
  const uploadGroupLogo = async (groupId, logoFile) => {
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      
      const response = await axios.post(`/api/groups/upload-logo/${groupId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return { success: true, logoUrl: response.data.logoUrl, message: response.data.message };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to upload group logo';
      return { success: false, error: errorMessage };
    }
  };

  // NEW: Delete group logo
  const deleteGroupLogo = async (groupId) => {
    try {
      const response = await axios.delete(`/api/groups/delete-logo/${groupId}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete group logo';
      return { success: false, error: errorMessage };
    }
  };

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const updateUserLocal = useCallback((userData) => {
    dispatch({ type: 'UPDATE_USER', payload: { ...state.user, ...userData } });
  }, [state.user]);

  return (
    <AuthContext.Provider value={{
      ...state,
      login,
      register,
      logout,
      updateUser,
      uploadPhoto,
      deletePhoto,
      createGroup,
      joinGroup,
      getMyGroup,
      getUserGroups,
      setActiveGroup,
      getPublicGroups,
      joinPublicGroup,
      leaveGroup,
      editGroup,
      deleteGroup,
      uploadGroupLogo,
      deleteGroupLogo,
      getEvents,
      clearError,
      updateUserLocal
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 